import React from "react";
import { message, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import GET_LANGUAGES from "graphql/queries/GetLanguages";
import GET_MY_SETTINGS from "graphql/queries/GetMySettings";
import UPDATE_PROFILE from "graphql/mutations/UpdateProfile";
import { Language, Level } from "types";
import { useQuery, useMutation } from "@apollo/client";
import { CustomButton, CardTitle, CustomCard } from "./AfterSignUpStyle";
import { ButtonCard, ListGroup } from "./LanguageLevelFormStyle";

interface TProps {
  setShowLanguageLevelForm: React.Dispatch<React.SetStateAction<boolean>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}
const LanguageLevelForm = (props: TProps) => {
  const { setStep, setShowLanguageLevelForm } = props;
  const { t } = useTranslation("after_sign_up");
  const { data: languageList } = useQuery(GET_LANGUAGES);
  const { data } = useQuery(GET_MY_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const studyLanguage = languageList?.languages.find((language: Language) => {
    return language.id === data?.me.currentLanguage.id;
  });
  const [updateMyLanguageLevel] = useMutation(UPDATE_PROFILE);
  const localeVietnam = data?.me.locale === "vi";
  const studyLanguageForVietnam = ["Japanese", "Korean", "Mandarin"].includes(
    studyLanguage?.englishName
  );

  const placementTestLink = studyLanguage?.placementTest;
  const handleOnClick = () => {
    window.open(placementTestLink, "_blank");
  };

  const handleClick = async (values: any, title: string): Promise<void> => {
    try {
      await updateMyLanguageLevel({
        variables: {
          input: {
            attributes: {
              currentLanguageId: studyLanguage?.id,
              currentLevelId: values,
            },
          },
        },
      });
    } catch (e) {
      message.error(t("Failed saving changes. Please try again."));
    } finally {
      message.success(
        t(`Your level was set as ${title}. You can change it in "My Settings".`)
      );
      setStep(3);
    }
  };

  const levels = studyLanguage?.levels;
  const getDescription = (level: Level) => {
    if (level.title) {
      const translatedDescription = t(level.title);
      if (translatedDescription !== level.title) {
        return translatedDescription;
      }
    }
    return level.description;
  };

  const handleOnClickNo = () => {
    handleClick(studyLanguage?.levels[0].id, studyLanguage?.levels[0].title);
  };
  const handleOnClickPrev = () => {
    setShowLanguageLevelForm(false);
  };
  const renderPlacementTest = () => (
    <div>
      <Typography.Title level={5} style={{ margin: "0 0 20px 0" }}>
        {t("Not sure of your level?")}
      </Typography.Title>
      <CustomButton className="toggle-btn placement" onClick={handleOnClick}>
        {t("Take a placement test").toUpperCase()}
      </CustomButton>
    </div>
  );
  return (
    <CustomCard className="step3">
      <CardTitle>
        <button type="button" className="icon-btn" onClick={handleOnClickPrev}>
          <FontAwesomeIcon
            icon={faArrowLeft as IconProp}
            className="icon"
            size="2x"
          />
        </button>
        {t("Select Your Level").toUpperCase()}
      </CardTitle>
      <CustomCard className="content">
        <Typography.Title level={5}>
          {t("Have you studied ")}
          {studyLanguage?.englishName}
          {t(" before?")}
        </Typography.Title>
        <div className="buttons">
          <CustomButton className="toggle-btn" onClick={handleOnClickNo}>
            {t("No, I have not").toUpperCase()}
          </CustomButton>
          <CustomButton className="toggle-btn yes">
            {t("Yes, I have").toUpperCase()}
          </CustomButton>
        </div>
        <Typography.Title level={5} style={{ margin: "20px 0 20px 0" }}>
          {t("Select a level")}
        </Typography.Title>
        <ListGroup>
          {levels?.map(
            (level: {
              id: number;
              title: string;
              description: string;
              active: boolean;
            }) => (
              <li
                className="list-group-item"
                style={{
                  display: "flex",
                }}
              >
                <ButtonCard
                  type="button"
                  onClick={() => handleClick(level.id, level.title)}
                  className="card-view"
                >
                  <div className="title"> {level.title} </div>
                  {level.description ? (
                    <div className="description">{getDescription(level)}</div>
                  ) : (
                    <div className="description" />
                  )}
                </ButtonCard>
              </li>
            )
          )}
        </ListGroup>
        {(!localeVietnam || !studyLanguageForVietnam) &&
          placementTestLink &&
          renderPlacementTest()}
      </CustomCard>
    </CustomCard>
  );
};

export default LanguageLevelForm;
